import { useEffect, useRef, useState } from 'react';
import type { NodeData } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';

export function FindBar() {
  const open = useApp('ui', () => app.ui.findOpen);
  useApp('doc', () => app.docVersion);
  const [q, setQ] = useState('');
  const [rep, setRep] = useState('');
  const [i, setI] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) requestAnimationFrame(() => input.current?.select());
  }, [open]);
  if (!open) return null;
  const needle = q.toLowerCase();
  const results: NodeData[] = needle
    ? app.doc.subtree(app.editor.pageId).filter((n) => n.type !== 'PAGE' && (n.name.toLowerCase().includes(needle) || (n.characters ?? '').toLowerCase().includes(needle)))
    : [];
  const go = (k: number) => {
    if (!results.length) return;
    const idx = ((k % results.length) + results.length) % results.length;
    setI(idx);
    app.editor.setSelection([results[idx].id]);
    app.zoomToNodes([results[idx].id], true, true);
  };
  const replaceIn = (n: NodeData) => {
    if (n.characters === undefined || !n.characters.toLowerCase().includes(needle)) return;
    const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    app.editor.update(n.id, { characters: n.characters.replace(re, rep) }, 'Replace text');
  };
  return (
    <div className="find-bar" onPointerDown={(e) => e.stopPropagation()}>
      <div className="row">
        <div className="field">
          <input
            ref={input}
            placeholder="Find layers and text…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setI(0);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') go(e.shiftKey ? i - 1 : results.length && app.editor.selection[0] === results[i]?.id ? i + 1 : i);
              if (e.key === 'Escape') app.setUI({ findOpen: false });
            }}
          />
          <span style={{ color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{q ? `${results.length ? i + 1 : 0}/${results.length}` : ''}</span>
        </div>
        <button className="icon-btn" onClick={() => go(i - 1)} title="Previous (⇧↵)">
          ↑
        </button>
        <button className="icon-btn" onClick={() => go(i + 1)} title="Next (↵)">
          ↓
        </button>
        <button className="icon-btn" onClick={() => app.setUI({ findOpen: false })}>
          {Icons.close}
        </button>
      </div>
      <div className="row">
        <div className="field">
          <input placeholder="Replace with…" value={rep} onChange={(e) => setRep(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
        </div>
        <button className="btn secondary" disabled={!results[i]} onClick={() => results[i] && replaceIn(results[i])}>
          Replace
        </button>
        <button className="btn secondary" disabled={!results.length} onClick={() => app.editor.transact('Replace all', () => results.forEach(replaceIn))}>
          All
        </button>
      </div>
      {results.length > 0 && (
        <div className="find-results">
          {results.slice(0, 50).map((n, k) => (
            <div key={n.id} className={`asset-row ${k === i ? 'on' : ''}`} onClick={() => go(k)}>
              <span className="name">{n.characters ? n.characters.slice(0, 60) : n.name}</span>
              <span className="meta">{n.type.toLowerCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
